'use client'
import SwapHorizIcon from '@mui/icons-material/SwapHoriz'
import SearchIcon from '@mui/icons-material/Search'
import {
    Box,
    Button,
    ClickAwayListener,
    Divider,
    FormControl,
    FormControlLabel,
    IconButton,
    Radio,
    RadioGroup
} from '@mui/material'
import { useTranslations } from 'next-intl'
import React, { useEffect, useState } from 'react'

import DatePicker from '@/src/components/desktop/srp/searchHeader/searchModifierInputs/CalendarWrapper/DatePicker'
import { FromToInput } from '@/src/components/desktop/srp/searchHeader/searchModifierInputs/fromToInput'
import { TravelersInput } from '@/src/components/desktop/srp/searchHeader/searchModifierInputs/TravelersInput'
import useHomepageGtmEvent from '@/src/hooks/analytics/gtm/homepage'
import DesktopMultiCitySearchForm from './components/DesktopMultiCitySearchForm'

interface DesktopSearchFormProps {
    searchManager: ReturnType<typeof import('./hooks/useHomePageSearchManager').useHomePageSearchManager>
    showError: (message: string) => void
    triggerSrpEvent: (eventName: string, data: any) => void
    hideMulticity?: boolean
}

const DesktopSearchForm: React.FC<DesktopSearchFormProps> = ({ searchManager, showError, triggerSrpEvent, hideMulticity }) => {
    const { pushSearchButtonClickEvent } = useHomepageGtmEvent()
    const {
        fromToInputsState,
        onAirportSelect,
        getAirportsFromQuery,
        handleSwap,
        isRoundTrip,
        isRoundTripDispatch,
        range,
        rangeDispatch,
        flightClassState,
        travelersState,
        handleTravelerChange,
        handleFlightClassChange,
        canIncreaseAdults,
        canIncreaseChildren,
        canIncreaseInfants,
        canDecreaseAdults,
        canDecreaseChildren,
        canDecreaseInfants,
        performHomePageSearch
    } = searchManager

    const t = useTranslations('common')

    // Trip type state: 'oneway' | 'roundtrip' | 'multicity'
    const [tripType, setTripType] = useState<'oneway' | 'roundtrip' | 'multicity'>(isRoundTrip ? 'roundtrip' : 'oneway')

    // Define the possible active component types
    type ActiveComponent = 'from' | 'to' | 'date' | 'travelers' | null
    const [activeComponent, setActiveComponent] = useState<ActiveComponent>(null)

    // Validation state for same airport error
    const [sameAirportError, setSameAirportError] = useState(false)

    // Check if from and to airports are the same
    useEffect(() => {
        const fromValue = fromToInputsState.from.value as string
        const toValue = fromToInputsState.to.value as string

        if (fromValue && toValue) {
            // Extract airport codes from the values
            const fromCode = fromValue.split(' - ')[0]
            const toCode = toValue.split(' - ')[0]

            setSameAirportError(fromCode === toCode && fromCode !== '')
        } else {
            setSameAirportError(false)
        }
    }, [fromToInputsState.from.value, fromToInputsState.to.value])

    // Centralized function to handle component activation
    const handleComponentActivation = (component: ActiveComponent) => {
        if (activeComponent === component && component !== 'from' && component !== 'to') {
            // If clicking the same component, close it (except for from/to inputs)
            setActiveComponent(null)
        } else {
            // If clicking a different component, switch to it
            setActiveComponent(component)
        }
    }

    // Function to close any active component
    const closeActiveComponent = () => {
        setActiveComponent(null)
    }

    // Handle click away - this should close any active component
    const handleClickAway = (event: Event | React.SyntheticEvent) => {
        // Close any active component when clicking outside
        if (activeComponent) {
            setActiveComponent(null)
        }
    }

    const swapHandler = () => {
        setActiveComponent(null)
        handleSwap()
    }

    // Handle search for home page - navigates to /flights/results
    const handleSearch = () => {
        // Don't search if there's a validation error
        if (sameAirportError) {
            showError(t('sameAirportError'))
            return
        }
        else if (!fromToInputsState.from.value || !fromToInputsState.to.value) {
            showError(t('pleaseFillAllRequiredFields'))
            return
        }
        setActiveComponent(null)

        // Push GTM event for successful search
        pushSearchButtonClickEvent({
            from: fromToInputsState.from.airportCode as string,
            to: fromToInputsState.to.airportCode as string,
            fromCity: fromToInputsState.from.city,
            toCity: fromToInputsState.to.city,
            departDate: searchManager.range.from,
            returnDate: searchManager.isRoundTrip ? searchManager.range.to : null,
            adults: searchManager.travelersState.adults,
            children: searchManager.travelersState.childs,
            infants: searchManager.travelersState.infants,
            flightClass: searchManager.flightClassState as string,
            isRoundTrip: searchManager.isRoundTrip
        })

        triggerSrpEvent('Air_UI_Action', {
            action_name: 'home_page_search',
            action_type: 'BUTTON',
            action_value: 'N/A'
        })

        performHomePageSearch()
    }

    // Helper functions to check if a specific component is active
    const isComponentActive = (component: ActiveComponent) => activeComponent === component

    useEffect(() => {
        if (sameAirportError) {
            showError(t('sameAirportError'))
        }
    }, [sameAirportError])

    // Handle trip type change
    const handleTripTypeChange = (type: 'oneway' | 'roundtrip' | 'multicity') => {
        // Prevent multicity if hidden
        if (hideMulticity && type === 'multicity') {
            return
        }
        setTripType(type)
        if (type === 'multicity') {
            // For multi-city, we don't need to update isRoundTrip
            return
        }
        isRoundTripDispatch(type === 'roundtrip')
    }

    const handleAddReturn = () => {
        isRoundTripDispatch(true)
        setTripType('roundtrip')
        // No need to set active component here, DatePicker will handle activation
    }

    return (
        <Box sx={{ position: 'relative', zIndex: 0 }} data-home-search-form>
            {/* Trip Type Radio Buttons - Above the search form */}
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-start' }}>
                <FormControl component="fieldset">
                    <RadioGroup
                        row
                        value={tripType}
                        onChange={e => handleTripTypeChange(e.target.value as 'oneway' | 'roundtrip' | 'multicity')}
                        sx={{
                            '& .MuiFormControlLabel-root': {
                                marginRight: 3,
                                '& .MuiRadio-root': {
                                    color: 'grey.500', // White for unselected state
                                    '&.Mui-checked': {
                                        color: '#3366cc' // Golden color for selected state
                                    }
                                },
                                '& .MuiFormControlLabel-label': {
                                    fontSize: '0.9rem',
                                    fontWeight: 500,
                                    color: '#000'
                                }
                            }
                        }}
                    >
                        <FormControlLabel value="oneway" control={<Radio />} label={t('oneWay')} />
                        <FormControlLabel value="roundtrip" control={<Radio />} label={t('roundTrip')} />
                        {!hideMulticity && <FormControlLabel value="multicity" control={<Radio />} label={t('multicity')} />}
                    </RadioGroup>
                </FormControl>
            </Box>

            {/* Conditional rendering based on trip type */}
            {tripType === 'multicity' ? (
                <DesktopMultiCitySearchForm searchManager={searchManager} showError={showError} />
            ) : (
                <ClickAwayListener onClickAway={handleClickAway}>
                    <Box
                        data-search-container
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-start',
                            overflow: 'hidden',
                            px: 1,
                            // bgcolor: '#ffffff',
                            // border: '1px solid rgb(229 231 235)',
                            // borderRadius: '0.75rem',
                            // boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
                            position: 'relative',
                            zIndex: 1
                        }}
                    >
                        {/* Origin */}
                        <Box
                            onClick={() => handleComponentActivation('from')}
                            sx={{
                                border: 1,
                                borderColor: 'grey.300',
                                borderRight: '1px solid rgb(229 231 235)',
                                display: 'flex',
                                alignItems: 'center',
                                minHeight: 70,
                                maxHeight: 70,
                                py: 2,
                                borderTopLeftRadius: '0.75rem',
                                borderBottomLeftRadius: '0.75rem',
                                position: 'relative',
                                flex: 1,
                                cursor: 'pointer'
                            }}
                        >
                            <FromToInput
                                srpResultsLoading={false}
                                key={`${fromToInputsState.from.value}-from` as string}
                                action="from"
                                initialValue={fromToInputsState.from.value as string}
                                loading={fromToInputsState.from.loading}
                                optionListItems={fromToInputsState.from.options}
                                queryOptionListItems={getAirportsFromQuery}
                                onOptionSelect={onAirportSelect}
                                active={isComponentActive('from')}
                                onActivate={() => handleComponentActivation('from')}
                                onClose={closeActiveComponent}
                                hasError={sameAirportError}
                                isPopularAirports={fromToInputsState.from.isPopularAirports}
                            />

                            {/* Swap Icon - Positioned at the right border center */}
                            <Box
                                sx={{
                                    position: 'absolute',
                                    right: -18,
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    zIndex: 2
                                }}
                            >
                                <IconButton
                                    sx={{
                                        border: '1px solid rgb(229 231 235)',
                                        borderRadius: '50%',
                                        width: 36,
                                        height: 36,
                                        bgcolor: '#ffffff',
                                        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
                                        '&:hover': {
                                            bgcolor: '#f9fafb'
                                        }
                                    }}
                                    size="small"
                                    onMouseDown={e => e.stopPropagation()}
                                    onClick={e => {
                                        e.stopPropagation()
                                        swapHandler()
                                    }}
                                >
                                    <SwapHorizIcon sx={{ fontSize: 18, color: 'grey.600' }} />
                                </IconButton>
                            </Box>
                        </Box>

                        {/* Destination */}
                        <Box
                            onClick={() => handleComponentActivation('to')}
                            sx={{
                                border: 1,
                                borderColor: 'grey.300',
                                borderLeft: '1px solid rgb(229 231 235)',
                                display: 'flex',
                                alignItems: 'center',
                                minHeight: 70,
                                maxHeight: 70,
                                py: 2,
                                position: 'relative',
                                flex: 1,
                                 cursor: 'pointer'
                            }}
                        >
                            <FromToInput
                                key={`${fromToInputsState.to.value}-to` as string}
                                action="to"
                                initialValue={fromToInputsState.to.value as string}
                                loading={fromToInputsState.to.loading}
                                optionListItems={fromToInputsState.to.options}
                                queryOptionListItems={getAirportsFromQuery}
                                onOptionSelect={onAirportSelect}
                                active={isComponentActive('to')}
                                onActivate={() => handleComponentActivation('to')}
                                onClose={closeActiveComponent}
                                hasError={sameAirportError}
                                isPopularAirports={fromToInputsState.to.isPopularAirports}
                            />
                        </Box>
                        {/* Date */}
                        <Box
                            sx={{
                                border: 1,
                                borderColor: 'grey.300',
                                borderLeft: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                minHeight: 70,
                                minWidth: 250,
                                p: 2
                            }}
                        >
                            <DatePicker
                                isRoundTrip={isRoundTrip}
                                range={range}
                                dateClick={rangeDispatch}
                                active={isComponentActive('date')}
                                onActivate={() => handleComponentActivation('date')}
                                onClose={closeActiveComponent}
                                onAddReturn={handleAddReturn}
                                showAddReturn={true}
                            />
                        </Box>

                        <Box
                            sx={{
                                border: 1,
                                borderColor: 'grey.300',
                                borderLeft: 0,
                                display: 'flex',
                                alignItems: 'center',
                                minHeight: 70,
                                maxHeight: 70,
                                p: 2
                            }}
                        >
                            <TravelersInput
                                adults={travelersState.adults}
                                childs={travelersState.childs}
                                infants={travelersState.infants}
                                flightClass={flightClassState as string}
                                onTravelerChange={handleTravelerChange}
                                onFlightClassChange={handleFlightClassChange}
                                canIncreaseAdults={canIncreaseAdults(travelersState)}
                                canIncreaseChildren={canIncreaseChildren(travelersState)}
                                canIncreaseInfants={canIncreaseInfants(travelersState)}
                                canDecreaseAdults={canDecreaseAdults(travelersState)}
                                canDecreaseChildren={canDecreaseChildren(travelersState)}
                                canDecreaseInfants={canDecreaseInfants(travelersState)}
                                active={isComponentActive('travelers')}
                                onActivate={() => handleComponentActivation('travelers')}
                                onClose={closeActiveComponent}
                            />
                        </Box>

                        {/* Search Button */}
                        <Box
                            sx={{
                                border: 1,
                                borderColor: 'grey.300',
                                borderLeft: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                minHeight: 70,
                                maxHeight: 70,
                                p: 2,
                                borderTopRightRadius: '0.75rem',
                                borderBottomRightRadius: '0.75rem'
                            }}
                        >
                            <Button
                                disabled={false}
                                variant="customVariantPrimary"
                                startIcon={<SearchIcon sx={{ color: 'white' }} />}
                                sx={{
                                    borderRadius: '0.75rem',
                                    p: 1.5,
                                    minWidth: 100,
                                }}
                                onClick={handleSearch}
                            >
                                {t('search')}
                            </Button>
                        </Box>
                    </Box>
                </ClickAwayListener>
            )}
        </Box>
    )
}

export default DesktopSearchForm
